import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isFunctionPillNode } from "components/CC/UI/TagInput/FunctionPillNode";
import {
    INSERT_RAW_TEXT_COMMAND,
    INSERT_TAG_COMMAND,
    type InsertRawTextPayload,
    type InsertTagPayload,
    type PrefixValidationLookup,
    type TagInputMode,
    type TagParamType,
    type ValidationLookup,
} from "components/CC/UI/TagInput/tagInputTypes";
import {
    type DisplayLabelLookup,
    type NodeTypeLookup,
    type StepLabelLookup,
    type StepNodeTypeLookup,
    ensureClickableGaps,
    inferParamType,
    INLINE_GAP_PLACEHOLDER,
    loadValueIntoEditor,
    parseInlineNodes,
    resolveDisplayLabel,
    resolveNodeType,
    resolveTagValidation,
    serializeEditorState,
    serializeInlineChildren,
} from "components/CC/UI/TagInput/tagInputUtils";
import { $createTagNode, $isTagNode, TagNode } from "components/CC/UI/TagInput/TagNode";
import {
    $createParagraphNode,
    $getNodeByKey,
    $getRoot,
    $getSelection,
    $isElementNode,
    $isLineBreakNode,
    $isRangeSelection,
    $isTextNode,
    COMMAND_PRIORITY_EDITOR,
    COMMAND_PRIORITY_HIGH,
    KEY_ENTER_COMMAND,
    ParagraphNode,
    TextNode,
    type ElementNode,
    type LexicalEditor,
    type LexicalNode,
} from "lexical";
import { useEffect, useRef, type MutableRefObject } from "react";

// ============================================================================
// EditorRefPlugin
// ============================================================================

export const EditorRefPlugin = ({ editorRef }: { editorRef: MutableRefObject<LexicalEditor | null> }): null => {
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        editorRef.current = editor;
        return () => {
            editorRef.current = null;
        };
    }, [editor, editorRef]);
    return null;
};

// ============================================================================
// BraceTransformPlugin
// ============================================================================

interface BraceTransformPluginProps {
    lookup: ValidationLookup;
    prefixLookup?: PrefixValidationLookup;
    mode: TagInputMode;
    onStrictRuleViolation?: () => void;
    /** Called right after a "{...}" span commits into a tag pill — lets the search field (mirrored
     * live from the raw text via BraceQueryPlugin) clear once there's nothing left to edit. */
    onTagCommitted?: () => void;
}

/** Replaces the `{inner}` span identified by `match` within `textNode` with a TagNode. Shared by
 * BraceTransformPlugin's node-transform and its Enter-key handler below, which both need to
 * perform the exact same split/replace regardless of what triggered the conversion. */
const replaceBraceMatchWithTagNode = (
    textNode: TextNode,
    match: RegExpExecArray,
    lookup: ValidationLookup,
    prefixLookup?: PrefixValidationLookup
): TagNode => {
    const text = textNode.getTextContent();
    const inner = match[1];
    const matchStart = match.index;
    const matchEnd = match.index + match[0].length;

    const resolved = resolveTagValidation(inner, lookup, prefixLookup);
    const tagNode = $createTagNode(inner, resolved.paramType, resolved.valid);

    if (matchStart === 0) {
        if (matchEnd === text.length) {
            textNode.replace(tagNode);
        } else {
            const [target] = textNode.splitText(matchEnd);
            target.replace(tagNode);
        }
    } else {
        if (matchEnd === text.length) {
            const [, target] = textNode.splitText(matchStart);
            if (target) target.replace(tagNode);
        } else {
            const [, target] = textNode.splitText(matchStart, matchEnd);
            if (target) target.replace(tagNode);
        }
    }

    return tagNode;
};

export const BraceTransformPlugin = ({
    lookup,
    prefixLookup,
    mode,
    onStrictRuleViolation,
    onTagCommitted,
}: BraceTransformPluginProps): null => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerNodeTransform(TextNode, (textNode) => {
            const text = textNode.getTextContent();

            // In tag-only mode, normalize {{x}} → {x} (strip both extra braces) before tag detection.
            if (mode === "tag-only") {
                const normalized = text.replace(/^\{\{([^{}]+)\}\}$/, "{$1}");
                if (normalized !== text) {
                    textNode.setTextContent(normalized);
                    return; // Transform will re-fire with the cleaned text
                }
            }

            const match = /\{([^{}]+)\}/.exec(text);
            if (!match) return;

            const matchStart = match.index;
            const matchEnd = match.index + match[0].length;

            // Skip while the cursor is inside the braces — user is editing the tag text.
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                const anchor = selection.anchor;
                if (anchor.type === "text" && anchor.key === textNode.getKey()) {
                    if (anchor.offset > matchStart && anchor.offset < matchEnd) {
                        return;
                    }
                }
            }

            if (mode === "strict-single") {
                const before = text.slice(0, matchStart).trim();
                const after = text.slice(matchEnd).trim();
                if (before || after) {
                    onStrictRuleViolation?.();
                    return;
                }
            }
            replaceBraceMatchWithTagNode(textNode, match, lookup, prefixLookup);
            onTagCommitted?.();
        });
    }, [editor, lookup, prefixLookup, mode, onStrictRuleViolation, onTagCommitted]);

    // Enter while the caret sits inside an in-progress "{...}" span (e.g. re-editing an existing
    // tag via TagNodeView's "Edit" action, which selects the whole inner span) must just close
    // editing and convert it back into a pill — never insert a line break there. Everywhere else,
    // Enter keeps its normal behavior (inserts a line break), which is why this returns false
    // outside that one case instead of swallowing every Enter press. Registered at HIGH priority
    // so it runs before Lexical's own default Enter handling.
    useEffect(() => {
        return editor.registerCommand(
            KEY_ENTER_COMMAND,
            (event) => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) return false;
                const anchorNode = selection.anchor.getNode();
                if (!$isTextNode(anchorNode)) return false;

                const text = anchorNode.getTextContent();
                const match = /\{([^{}]+)\}/.exec(text);
                if (!match) return false;

                const matchStart = match.index;
                const matchEnd = match.index + match[0].length;
                const offset = selection.anchor.offset;
                if (offset < matchStart || offset > matchEnd) return false;

                // A non-collapsed selection that starts inside the brace span but ends outside it
                // (e.g. shift-selecting from mid-tag out into surrounding text) must fall through to
                // Enter's normal behavior instead of hijacking the selected range into a conversion.
                const focusNode = selection.focus.getNode();
                const focusOffset = selection.focus.offset;
                if (focusNode.getKey() !== anchorNode.getKey() || focusOffset < matchStart || focusOffset > matchEnd) {
                    return false;
                }

                event?.preventDefault();

                if (mode === "strict-single") {
                    const before = text.slice(0, matchStart).trim();
                    const after = text.slice(matchEnd).trim();
                    if (before || after) {
                        onStrictRuleViolation?.();
                        return true;
                    }
                }

                replaceBraceMatchWithTagNode(anchorNode, match, lookup, prefixLookup);
                onTagCommitted?.();
                return true;
            },
            COMMAND_PRIORITY_HIGH
        );
    }, [editor, lookup, prefixLookup, mode, onStrictRuleViolation, onTagCommitted]);

    return null;
};

// ============================================================================
// BraceQueryPlugin
// ============================================================================

interface BraceQueryPluginProps {
    onQueryChange: (query: string) => void;
}

/**
 * Mirrors the text typed inside an in-progress "{...}" span into `onQueryChange` on every
 * keystroke/caret move, so ParameterPicker's search field can live-filter as the user types
 * instead of requiring a separate click into the picker's own search input. Reads the same
 * in-progress span as BraceTransformPlugin's "skip while caret inside braces" guard, but runs
 * independently via a plain update listener — it never converts anything, so it can't interfere
 * with that guard or the conversion it protects.
 */
export const BraceQueryPlugin = ({ onQueryChange }: BraceQueryPluginProps): null => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            let query: string | null = null;

            editorState.read(() => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) return;

                // Both ends must sit in the same text node — covers a plain collapsed caret as
                // well as a full-span selection like TagNodeView's "Edit" action, which selects
                // the whole reconstituted "{inner}" span so the user can immediately retype it.
                const anchorNode = selection.anchor.getNode();
                const focusNode = selection.focus.getNode();
                if (!$isTextNode(anchorNode) || focusNode.getKey() !== anchorNode.getKey()) return;

                // Unlike BraceTransformPlugin's conversion regex (which requires at least one
                // character before it'll promote "{...}" into a tag), this one also matches an
                // empty "{}" pair — deleting a tag's content back down to nothing must still
                // report an empty query, not silently keep mirroring whatever was typed last.
                const text = anchorNode.getTextContent();
                const match = /\{([^{}]*)\}/.exec(text);
                if (!match) return;

                const matchStart = match.index;
                const matchEnd = match.index + match[0].length;
                const withinSpan = (offset: number) => offset > matchStart && offset < matchEnd;
                if (withinSpan(selection.anchor.offset) && withinSpan(selection.focus.offset)) {
                    query = match[1];
                }
            });

            // Caret outside any in-progress brace span — leave the search value as-is (don't
            // clear it), it only ever gets overwritten once a brace span is being edited again.
            if (query !== null) {
                onQueryChange(query);
            }
        });
    }, [editor, onQueryChange]);

    return null;
};

// ============================================================================
// FunctionCallTransformPlugin
// ============================================================================

// Coalesces adjacent TextNode runs into a single "T:..." entry, so a TextNode that later gets
// split (e.g. by an unrelated transform) never counts as a shape change purely because of how
// many sibling nodes its same combined text happens to be spread across. FunctionPillNode
// contributes its own kind+name — that identity is the only thing encoding which "(" pairs with
// which ")" now that there's no wrapper node grouping them.
const shapeSignature = (nodes: LexicalNode[]): string => {
    const parts: string[] = [];
    let textBuffer = "";
    const flushText = () => {
        if (textBuffer) {
            parts.push(`T:${textBuffer}`);
            textBuffer = "";
        }
    };
    nodes.forEach((node) => {
        if ($isFunctionPillNode(node)) {
            flushText();
            // isUnclosed() must be part of the identity: an "open" pill going from unclosed to
            // closed (or back) can leave the surrounding entry count unchanged (e.g. deleting the
            // ")" pill right after a valid closed pair leaves just the one "P:open:name" entry,
            // identical in shape to the freshly-unclosed reparse of that same text) — without this,
            // the stale pre-edit node gets kept as-is instead of being swapped for the reparsed one
            // that actually reflects the new unclosed state.
            parts.push(`P:${node.__kind}:${node.__name}:${node.isUnclosed() ? "u" : "c"}`);
        } else if ($isTagNode(node)) {
            flushText();
            parts.push(`G:${node.getInner()}`);
        } else if ($isLineBreakNode(node)) {
            // Must be part of the identity, not silently dropped — otherwise "abc\ndef" and "abcdef"
            // hash to the same signature (both flush to a single "T:abcdef" entry), so a real,
            // deliberate line break the user just typed would come back as "no shape change" and
            // never get reflected in the reparsed replacement.
            flushText();
            parts.push("BR");
        } else if ($isTextNode(node)) {
            textBuffer += node.getTextContent().split(INLINE_GAP_PLACEHOLDER).join("");
        }
    });
    flushText();
    return parts.join("|");
};

/** Length of a node's raw-text representation (TagNode's/FunctionPillNode's real DecoratorNode
 * getTextContent() already returns their serialized form directly; a TextNode's invisible
 * INLINE_GAP_PLACEHOLDER filler doesn't count — it's not part of the reported value; a
 * LineBreakNode's getTextContent() is "\n", matching the one "\n" character it contributes to the
 * serialized line via serializeInlineChildren). */
const flatLength = (node: LexicalNode): number => {
    if ($isTagNode(node)) return `{${node.getInner()}}`.length;
    if ($isTextNode(node)) return node.getTextContent().split(INLINE_GAP_PLACEHOLDER).join("").length;
    return node.getTextContent().length;
};

/**
 * Selects the TextNode at flat offset `targetOffset` within `nodes`. Landing exactly on an atomic
 * node's edge (TagNode/FunctionPillNode — e.g. right after a call's closing pill) has no
 * sub-position to select into, so it selects immediately before/after that node instead of
 * silently giving up.
 */
const selectAtFlatOffset = (nodes: LexicalNode[], targetOffset: number): boolean => {
    let remaining = targetOffset;
    for (const node of nodes) {
        const len = flatLength(node);
        if (remaining <= len) {
            if ($isTextNode(node)) {
                const offset = Math.min(remaining, node.getTextContent().length);
                node.select(offset, offset);
                return true;
            }
            if (remaining <= 0) {
                node.selectPrevious();
            } else {
                node.selectNext();
            }
            return true;
        }
        remaining -= len;
    }
    return false;
};

/**
 * Wraps completed `functionName(...)` spans (Format & convert / Math Functions) into a matched
 * pair of flat FunctionPillNode markers — no wrapper element, so every pill behaves exactly like
 * a plain TagNode (click, drag, arrow-key nav all come for free). Re-parses this paragraph's own
 * flat text via parseInlineNodes and only replaces children when the resulting shape actually
 * differs — keeps typing idempotent (no per-keystroke node churn, no infinite transform loop).
 */
const scanElementForFunctionCalls = (element: ElementNode): void => {
    const currentChildren = element.getChildren();

    // Mirrors BraceTransformPlugin's own "cursor inside braces" guard: parseInlineNodes converts
    // any complete "{tag}" span into a TagNode with no notion of in-progress editing, so without
    // this check it would eagerly promote e.g. "{parsedBody.}" the instant that raw-text
    // placeholder is inserted (cursor sitting right before the "}", still meant to be typed into)
    // — racing BraceTransformPlugin's own guarded conversion and winning, since this scan has no
    // such guard of its own. Deliberately checks only the anchor offset, not selectionForGuard's
    // collapsed-ness — TagNodeView's "Edit" action selects the whole inner span (a non-collapsed
    // range) so the user can immediately retype it, and that selection must be guarded too, same
    // as BraceTransformPlugin's own version of this check does.
    const selectionForGuard = $getSelection();
    if ($isRangeSelection(selectionForGuard)) {
        const anchorNode = selectionForGuard.anchor.getNode();
        if ($isTextNode(anchorNode) && currentChildren.some((child) => child.is(anchorNode))) {
            const text = anchorNode.getTextContent();
            const match = /\{([^{}]+)\}/.exec(text);
            if (match) {
                const offset = selectionForGuard.anchor.offset;
                if (offset > match.index && offset < match.index + match[0].length) {
                    return;
                }
            }
        }
    }

    const currentSignature = shapeSignature(currentChildren);
    const parsed = ensureClickableGaps(parseInlineNodes(serializeInlineChildren(element)));

    if (shapeSignature(parsed) !== currentSignature) {
        const selection = $getSelection();
        const anchor = $isRangeSelection(selection) ? selection.anchor : null;
        const anchorNode = anchor?.getNode() ?? null;

        // Only track the caret when it sits directly in one of this element's own TextNode
        // children — covers the common typing case. Deeper/other selections just fall through
        // to Lexical's default post-mutation selection repair.
        let flatCaretOffset: number | null = null;
        if (anchor && $isTextNode(anchorNode)) {
            let offset = 0;
            for (const child of currentChildren) {
                if (child.is(anchorNode)) {
                    flatCaretOffset = offset + anchor.offset;
                    break;
                }
                offset += flatLength(child);
            }
        }

        element.clear();
        parsed.forEach((node) => element.append(node));

        if (flatCaretOffset !== null) {
            selectAtFlatOffset(element.getChildren(), flatCaretOffset);
        }
    }
};

export const FunctionCallTransformPlugin = (): null => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        const unregisterParagraph = editor.registerNodeTransform(ParagraphNode, scanElementForFunctionCalls);
        // Appending characters into an existing TextNode only dirties that leaf, not its parent
        // paragraph — so the transform above alone would miss most keystrokes. Registering on
        // TextNode too (scanning its parent) makes every keystroke re-check for a completed or
        // newly-unclosed function call.
        const unregisterText = editor.registerNodeTransform(TextNode, (textNode) => {
            const parent = textNode.getParent();
            if (parent) scanElementForFunctionCalls(parent);
        });
        return () => {
            unregisterParagraph();
            unregisterText();
        };
    }, [editor]);

    return null;
};

// ============================================================================
// TagValidationPlugin
// ============================================================================

interface TagValidationPluginProps {
    lookup: ValidationLookup;
    prefixLookup?: PrefixValidationLookup;
    displayLabelLookup?: DisplayLabelLookup;
    stepLabelLookup?: StepLabelLookup;
    nodeTypeLookup?: NodeTypeLookup;
    stepNodeTypeLookup?: StepNodeTypeLookup;
    onInvalidTagsChange?: (invalidTags: string[]) => void;
    onDebugTagsChange?: (
        tags: {
            inner: string;
            valid: boolean;
            paramType: TagParamType;
        }[]
    ) => void;
}

export const TagValidationPlugin = ({
    lookup,
    prefixLookup,
    displayLabelLookup,
    stepLabelLookup,
    nodeTypeLookup,
    stepNodeTypeLookup,
    onInvalidTagsChange,
    onDebugTagsChange,
}: TagValidationPluginProps): null => {
    const [editor] = useLexicalComposerContext();
    const previousInvalidRef = useRef<string>("");

    useEffect(() => {
        const unregisterNodeTransform = editor.registerNodeTransform(TagNode, (tagNode) => {
            const inner = tagNode.getInner();
            const resolved = resolveTagValidation(inner, lookup, prefixLookup);
            const currentParamType = tagNode.getParamType() ?? inferParamType(inner);
            const currentValid = tagNode.isValid();
            if (currentValid !== resolved.valid || currentParamType !== resolved.paramType) {
                tagNode.setValidation(resolved.valid, resolved.paramType);
            }
            if (displayLabelLookup || stepLabelLookup) {
                const resolvedLabel = resolveDisplayLabel(inner, displayLabelLookup, stepLabelLookup);
                const currentLabel = tagNode.getLatest().__displayLabel;
                if (resolvedLabel !== currentLabel) {
                    tagNode.setDisplayLabel(resolvedLabel);
                }
            }
            if (nodeTypeLookup || stepNodeTypeLookup) {
                const resolvedNodeType = resolveNodeType(inner, nodeTypeLookup, stepNodeTypeLookup);
                const currentNodeType = tagNode.getLatest().__nodeType;
                if (resolvedNodeType !== currentNodeType) {
                    tagNode.setNodeType(resolvedNodeType);
                }
            }
        });

        const collectTags = (node: LexicalNode, result: TagNode[]) => {
            if ($isTagNode(node)) {
                result.push(node);
                return;
            }
            if ($isElementNode(node)) {
                node.getChildren().forEach((child) => collectTags(child, result));
            }
        };

        const unregisterUpdateListener = editor.registerUpdateListener(({ editorState }) => {
            const updates: {
                nodeKey: string;
                valid: boolean;
                paramType: Exclude<TagParamType, undefined>;
                displayLabel: string | undefined;
                nodeType: string | undefined;
            }[] = [];
            const invalidTags: string[] = [];
            const debugTags: {
                inner: string;
                valid: boolean;
                paramType: TagParamType;
            }[] = [];

            editorState.read(() => {
                const tags: TagNode[] = [];
                collectTags($getRoot(), tags);

                tags.forEach((tagNode) => {
                    const inner = tagNode.getInner();
                    const resolved = resolveTagValidation(inner, lookup, prefixLookup);
                    const currentParamType = tagNode.getParamType() ?? inferParamType(inner);
                    const currentValid = tagNode.isValid();
                    const hasDisplayLabelSource = displayLabelLookup !== undefined || stepLabelLookup !== undefined;
                    const resolvedDisplayLabel = resolveDisplayLabel(inner, displayLabelLookup, stepLabelLookup);
                    const currentDisplayLabel = tagNode.getLatest().__displayLabel;
                    const hasNodeTypeSource = nodeTypeLookup !== undefined || stepNodeTypeLookup !== undefined;
                    const resolvedNodeType = resolveNodeType(inner, nodeTypeLookup, stepNodeTypeLookup);
                    const currentNodeType = tagNode.getLatest().__nodeType;
                    debugTags.push({ inner, valid: currentValid, paramType: currentParamType });

                    if (!resolved.valid) {
                        invalidTags.push(inner);
                    }

                    if (
                        currentValid !== resolved.valid ||
                        currentParamType !== resolved.paramType ||
                        (hasDisplayLabelSource && resolvedDisplayLabel !== currentDisplayLabel) ||
                        (hasNodeTypeSource && resolvedNodeType !== currentNodeType)
                    ) {
                        updates.push({
                            nodeKey: tagNode.getKey(),
                            valid: resolved.valid,
                            paramType: resolved.paramType,
                            displayLabel: hasDisplayLabelSource ? resolvedDisplayLabel : currentDisplayLabel,
                            nodeType: hasNodeTypeSource ? resolvedNodeType : currentNodeType,
                        });
                    }
                });
            });

            if (updates.length > 0) {
                editor.update(() => {
                    updates.forEach((update) => {
                        const node = $getNodeByKey(update.nodeKey);
                        if ($isTagNode(node)) {
                            node.setValidation(update.valid, update.paramType);
                            node.setDisplayLabel(update.displayLabel);
                            node.setNodeType(update.nodeType);
                        }
                    });
                });
            }

            const deduped = Array.from(new Set(invalidTags));
            const invalidHash = deduped.join("|");
            if (previousInvalidRef.current !== invalidHash) {
                previousInvalidRef.current = invalidHash;
                onInvalidTagsChange?.(deduped);
            }
            onDebugTagsChange?.(debugTags);
        });

        return () => {
            unregisterNodeTransform();
            unregisterUpdateListener();
        };
    }, [
        editor,
        lookup,
        prefixLookup,
        displayLabelLookup,
        stepLabelLookup,
        nodeTypeLookup,
        stepNodeTypeLookup,
        onInvalidTagsChange,
        onDebugTagsChange,
    ]);

    return null;
};

// ============================================================================
// InitialValuePlugin
// ============================================================================

interface InitialValuePluginProps {
    value: string;
    mode?: TagInputMode;
}

export const InitialValuePlugin = ({ value, mode }: InitialValuePluginProps): null => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        const currentValue = serializeEditorState(editor.getEditorState());
        if (currentValue === value) return;
        // The "{}" scaffold is the local equivalent of an empty value — don't let it get wiped.
        if (mode === "tag-only" && currentValue === "{}" && value === "") return;
        loadValueIntoEditor(editor, value);
    }, [editor, value, mode]);

    return null;
};

// ============================================================================
// InsertTagCommandPlugin
// ============================================================================

interface InsertTagCommandPluginProps {
    lookup: ValidationLookup;
    prefixLookup?: PrefixValidationLookup;
}

export const InsertTagCommandPlugin = ({ lookup, prefixLookup }: InsertTagCommandPluginProps): null => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerCommand<InsertTagPayload>(
            INSERT_TAG_COMMAND,
            ({ inner, paramType, replaceAll, displayLabel, nodeType }) => {
                const selection = $getSelection();
                const resolved = resolveTagValidation(inner, lookup, prefixLookup);
                const tagNode = $createTagNode(
                    inner,
                    paramType ?? resolved.paramType,
                    resolved.valid,
                    displayLabel,
                    nodeType
                );

                if (replaceAll) {
                    const root = $getRoot();
                    root.clear();
                    const paragraph = $createParagraphNode();
                    paragraph.append(tagNode);
                    root.append(paragraph);
                    return true;
                }

                if ($isRangeSelection(selection)) {
                    selection.insertNodes([tagNode]);
                } else {
                    const root = $getRoot();
                    const lastParagraph = root.getLastChild();
                    if (lastParagraph && $isElementNode(lastParagraph)) {
                        lastParagraph.append(tagNode);
                    }
                }
                return true;
            },
            COMMAND_PRIORITY_EDITOR
        );
    }, [editor, lookup, prefixLookup]);

    return null;
};

/**
 * Inserts literal, uncommitted text at the caret — used for placeholders the user still needs to
 * fill in, e.g. selecting "toBool" inserts "toBool()" with the caret between the parentheses, or
 * selecting "parsedBody" inserts "{step.parsedBody.}" with the caret between "." and "}" so the
 * BraceTransformPlugin's "cursor inside braces" guard leaves it as raw text instead of converting
 * it into a tag pill right away.
 */
export const InsertRawTextCommandPlugin = (): null => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerCommand<InsertRawTextPayload>(
            INSERT_RAW_TEXT_COMMAND,
            ({ text, cursorOffset, replaceAll }) => {
                const trailingLength = text.length - cursorOffset;

                if (replaceAll) {
                    const root = $getRoot();
                    root.clear();
                    const paragraph = $createParagraphNode();
                    const textNode = new TextNode(text);
                    paragraph.append(textNode);
                    root.append(paragraph);
                    textNode.select(cursorOffset, cursorOffset);
                    return true;
                }

                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    selection.insertText(text);
                    const afterInsertSelection = $getSelection();
                    if ($isRangeSelection(afterInsertSelection)) {
                        const anchorNode = afterInsertSelection.anchor.getNode();
                        const offset = afterInsertSelection.anchor.offset - trailingLength;
                        if ($isTextNode(anchorNode)) {
                            anchorNode.select(offset, offset);
                        }
                    }
                } else {
                    const root = $getRoot();
                    const lastParagraph = root.getLastChild();
                    if (lastParagraph && $isElementNode(lastParagraph)) {
                        const textNode = new TextNode(text);
                        lastParagraph.append(textNode);
                        textNode.select(cursorOffset, cursorOffset);
                    }
                }
                return true;
            },
            COMMAND_PRIORITY_EDITOR
        );
    }, [editor]);

    return null;
};
